import { p as proxyCustomElement, H, c as createEvent, f as forceUpdate, h, d as Host } from './p-bfa0dc67.js';
import { c as classNames } from './p-df8c8ec2.js';
import { i as inheritAriaAttributes } from './p-198613ef.js';
import { L as Label, l as labelId, d as descriptionId } from './p-8097ff18.js';
import { m as messageId, V as Validation } from './p-319941a1.js';
import { d as defineCustomElement$2 } from './p-fbb8407b.js';

const selectCss = ".sr-only{position:absolute;height:1px;width:1px;padding:0;margin:-1px;overflow:hidden;border:0;left:-9999px;text-indent:-5000px;clip:rect(1px, 1px, 1px, 1px);}*,*:before,*:after{box-sizing:border-box}html,body,div,span,object,iframe,figure,h1,h2,h3,h4,h5,h6,p,blockquote,pre,a,code,em,img,small,strike,strong,sub,sup,tt,b,u,i,ol,ul,li,fieldset,form,label,table,caption,tbody,tfoot,thead,tr,th,td,main,canvas,embed,footer,header,nav,section,button,video{margin:0;padding:0;border:0;font-size:100%;font:inherit;vertical-align:baseline;text-rendering:optimizeLegibility;-webkit-font-smoothing:antialiased;text-size-adjust:none}footer,header,nav,section,main{display:block}body{line-height:1.5}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:before,blockquote:after,q:before,q:after{content:\"\";content:none}table{border-collapse:collapse;border-spacing:0}input{-webkit-appearance:none;appearance:none;border-radius:0}:host(:focus){outline:none !important;border:none !important}.label-wrapper{display:flex;flex-direction:column;gap:4px;height:fit-content;margin-bottom:8px}.label-wrapper .label{color:#414651;font-size:1rem;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;height:fit-content}.label-wrapper .label.fontStyle--regular{font-weight:500}.label-wrapper .label.fontStyle--bold{font-weight:700}.label-wrapper .required{color:#8c0a08;margin-left:8px}.label-wrapper .label+.label{font-family:\"museo-sans\", arial, sans-serif;color:#717680;font-size:0.75rem;line-height:1.5}.label-wrapper.dark .label{color:whitesmoke}.label-wrapper.dark .required{color:#ec9a98}.label-wrapper.dark .label+.label{color:#e9eaeb}.label-wrapper.noMargin{margin-bottom:0}.validation-message{font-size:1rem;color:#8c0a08;line-height:1.5;font-family:\"museo-sans\", arial, sans-serif;width:100%;height:1.5rem;margin-top:12px;margin-bottom:12px;display:block}.validation-message.dark{color:#ec9a98}.validation-message.noMargin{margin:0}fieldset{min-inline-size:unset}div.outer-wrapper{max-width:520px;width:100%}div.outer-wrapper.stretch{max-width:none}.select>button{border-radius:4px;padding:12px;display:flex;gap:12px;justify-content:space-between;background-color:white;border:1.5px solid #01417b;width:100%;text-align:start;cursor:pointer;font-size:1rem;line-height:1.5}.select>button>span,.select>button div{overflow:hidden;overflow-wrap:break-word}.select>button>span{overflow-wrap:unset;text-overflow:ellipsis;white-space:nowrap}.select>button.stretch{max-width:none}.select>button.flat-bottom{border-radius:4px 4px 0 0}.select>button:disabled{opacity:0.3;cursor:default}.select>button:hover:not(:disabled){background-color:#cfebff}.select>button.warning{border-color:#8c0a08}.select>button:focus-visible{border-radius:4px;outline-offset:2px;outline:1.5px solid var(--bi-primary-50);box-shadow:0 0 0 2px white}.select>button bi-icon-keyboard-arrow-down.open{transform:rotate(180deg)}.outer-wrapper{position:relative;width:100%}.outer-wrapper select{transform:translate(-175%, -100%);position:absolute;pointer-events:none;opacity:0;margin:0px;width:16px;height:16px}.outer-wrapper.small .select{height:100%}.outer-wrapper.small .select>button{height:36px;padding:0 12px;min-height:100%;align-items:center;font-size:0.75rem;line-height:1.5}.outer-wrapper.small .option-wrapper{min-width:auto}.outer-wrapper .option-wrapper{position:absolute;width:100%;background-color:white;border:1.5px solid #01417b;border-radius:0 0 4px 4px;border-top:none;flex-direction:column;z-index:10;opacity:0;transform:translateY(-10px);transition:opacity 0.1s ease, transform 0.1s ease;max-height:240px;overflow-y:auto;display:none}.outer-wrapper .option-wrapper.open{display:flex;opacity:1;transform:translateY(0);box-shadow:0 3px 4px 0px rgba(0, 0, 0, 0.25)}";
const BiSelectStyle0 = selectCss;

const BiSelect$1 = /*@__PURE__*/ proxyCustomElement(class BiSelect extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.change = createEvent(this, "change", 7);
        this.blur = createEvent(this, "blur", 7);
        this.internals = this.attachInternals?.();
        this.inheritedAttributes = {};
        // The "name" property is reflected as an attribute to ensure compatibility with native form submission.
        // In the React wrapper, all props are synced as properties on the element ref, so reflecting "name" as an attribute ensures it is properly handled in the form submission process.
        /** You can set the initial value of the select-field. Don't set this if you're also setting the `value`-prop */
        this.defaultValue = '';
        /** Marks the select field as required. */
        this.required = false;
        /** Marks the select field as disabled. */
        this.disabled = false;
        /** Stretch the select to use full available width. */
        this.stretch = false;
        /** Truncate the value if it's too wide. Wraps it to a new line by default */
        this.truncate = false;
        /** Size of the select field */
        this.size = 'medium';
        /** The placeholder text. */
        this.placeholder = '';
        /**
         * If `true`, the component will indicate an error.
         * @default false
         */
        this.error = false;
        this.open = false;
        this.selectOptions = [];
        this.onBlur = (e) => {
            e.stopPropagation();
            e.stopImmediatePropagation();
            this.blur.emit(e);
        };
        this.onSlotchange = () => {
            this.updateOptions();
        };
        this.updateOptions = () => {
            this.selectOptions = this.getOptions();
        };
        this.handleClickOutside = (event) => {
            const isClickInside = this.el.contains(event.target);
            if (!isClickInside) {
                this.open = false;
            }
        };
        this.getOptions = () => Array.from(this.el.querySelectorAll('bi-select-option'));
        this.getSelectedOption = (options) => options.find((option) => option.selected);
        this.getDisplayValue = (options) => {
            const selectedOption = this.getSelectedOption(options);
            return selectedOption?.label || this.placeholder;
        };
        this.toggleComponent = (e) => {
            e.preventDefault();
            this.open = !this.open;
        };
        this.onFocusout = (event) => {
            if (this.el.contains(event.relatedTarget) || this.el.shadowRoot.contains(event.relatedTarget)) {
                return;
            }
            this.open = false;
        };
        this.onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                this.buttonElement.focus();
                this.open = false;
            }
        };
    }
    get type() {
        return 'select';
    }
    /** A readonly property to get the options of the select */
    get options() {
        return this.getOptions();
    }
    /** Get and set the selected options of the select. */
    get selectedOptions() {
        return this.options.filter((item) => item.selected);
    }
    async onOptionChange(value, initialLoad = false) {
        this.updateOptions();
        this.value = value;
        if (!initialLoad) {
            this.change.emit();
        }
        await Promise.all(this.selectOptions.map((option) => option.updateSelected(option.value === value)));
        if (this.open) {
            // Move focus to the button when closing the select to prevent that a hidden option is focused
            this.buttonElement.focus();
            this.open = false;
        }
    }
    async onValueChange(newValue) {
        this.updateOptions();
        // The `selected` field on the options is outdated and must be
        // updated when the value of the select is changed directly
        if (this.getSelectedOption(this.selectOptions)?.value !== newValue) {
            this.selectOptions.forEach((option) => option.updateSelected(option.value === newValue));
        }
        this.internals?.setFormValue(newValue);
    }
    async componentWillLoad() {
        if (this.value !== undefined) {
            this.defaultValue = this.value;
        }
        else {
            this.value = this.defaultValue ?? '';
        }
        this.internals?.setFormValue(this.value);
        this.inheritedAttributes = inheritAriaAttributes(this.el);
    }
    formResetCallback() {
        this.onOptionChange(this.defaultValue);
    }
    formStateRestoreCallback(state) {
        this.onOptionChange(state);
    }
    async componentDidLoad() {
        this.internals?.setFormValue(this.value);
        document.addEventListener('click', this.handleClickOutside);
        if (this.value) {
            await this.onOptionChange(this.value, true);
            forceUpdate(this.el);
        }
    }
    disconnectedCallback() {
        document.removeEventListener('click', this.handleClickOutside);
    }
    render() {
        const Element = this.truncate ? 'span' : 'div';
        const id = 'select';
        const listId = 'list';
        return (h(Host, { key: '0e657cb5b34d1ff0c0dbdd631997a407c85c61c5' }, h("fieldset", { key: '06417a00b9fc93c5f8fc7e0e0101a03c30a37911', onKeyDown: this.onKeyDown, onBlur: this.onBlur, onFocusout: this.onFocusout }, h("div", { key: '9bd602620335915c9152bdb6366fd208b21890a2', class: classNames('outer-wrapper', this.size, this.stretch && 'stretch') }, h(Label, { key: 'e06ca32ccf458b43c11150d49186d8698c2d71fc', htmlFor: id, label: this.label, helptext: this.helptext, required: this.required, disabled: this.disabled }), h("div", { key: 'dd28a64e019069e00733ec6e38ca95ed7e4c7e7f', class: 'select' }, h("button", { key: 'c5843a400c9968a4da8c997217c414c3c0689049', ref: (el) => (this.buttonElement = el), id: id, "aria-labelledby": labelId, "aria-describedby": `${descriptionId} ${messageId}`, "aria-invalid": String(this.error), "aria-haspopup": 'listbox', "aria-expanded": String(this.open), "aria-required": String(this.required), "aria-controls": listId, disabled: this.disabled, type: 'button', role: 'combobox', class: classNames({ 'flat-bottom': this.open }, { warning: this.error }), onClick: this.toggleComponent, ...this.inheritedAttributes }, h(Element, { key: '39a78758478c0f14f1b011c191e5a40407e67a16' }, this.getDisplayValue(this.selectOptions)), h("bi-icon-chevron-down", { key: '7ce1f2ec0486ee615ccf389114d3e58e4fee2e75', "aria-hidden": 'true', color: 'black', size: 'medium', class: classNames({ open: this.open }) }))), h("div", { key: '8b7116195628e6c35d571743fea7c685e1a41296', class: classNames('option-wrapper', this.open ? 'open' : 'hidden'), role: 'listbox', "aria-label": this.label, "aria-hidden": String(!this.open), id: listId }, h("slot", { key: '3a880c94e793aeb506390f1cd8dd38eaa88cfbdb', onSlotchange: this.onSlotchange })), h(Validation, { key: 'e478258447788044343bc45f7dbcc243861677ce', visible: this.error, message: this.errorMessage })))));
    }
    static get delegatesFocus() { return true; }
    static get formAssociated() { return true; }
    get el() { return this; }
    static get watchers() { return {
        "value": ["onValueChange"]
    }; }
    static get style() { return BiSelectStyle0; }
}, [81, "bi-select", {
        "type": [2049],
        "options": [2064],
        "selectedOptions": [2064],
        "label": [1],
        "helptext": [1],
        "name": [513],
        "defaultValue": [1, "default-value"],
        "value": [1025],
        "required": [4],
        "disabled": [4],
        "stretch": [4],
        "truncate": [516],
        "size": [1],
        "placeholder": [1],
        "errorMessage": [1, "error-message"],
        "error": [4],
        "open": [32],
        "onOptionChange": [64]
    }, undefined, {
        "value": ["onValueChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["bi-select", "bi-icon-chevron-down"];
    components.forEach(tagName => { switch (tagName) {
        case "bi-select":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, BiSelect$1);
            }
            break;
        case "bi-icon-chevron-down":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const BiSelect = BiSelect$1;
const defineCustomElement = defineCustomElement$1;

export { BiSelect, defineCustomElement };
